import SettingsMenu from "views/admin/main/account/component/SettingsMenu";
import SaveStatus from "components/actions/SaveStatus";
import React, { useEffect, useState } from 'react';
import { Check, Package, Calendar, MousePointer, Truck, BellOff, Tag, MapPin, Percent } from 'lucide-react';
import TagList from "components/tagProduct/tagList";
import ZoneList from "components/zone/zoneList";
import VatAccountingCodeList from "components/vatAccountingCode/vatAccountingCodeList";
import { getMyInfo, updateMyInfo } from "interfaces/user";
import { toast } from "react-toastify";

type TabId = 'stock' | 'crm' | 'tags' | 'zones' | 'vat';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'stock', label: 'Règles de stock', icon: <Package className="w-4 h-4" /> },
  { id: 'crm', label: 'Relance clients', icon: <BellOff className="w-4 h-4" /> },
  { id: 'tags', label: 'Tags produits', icon: <Tag className="w-4 h-4" /> },
  { id: 'zones', label: 'Zones de stock', icon: <MapPin className="w-4 h-4" /> },
  { id: 'vat', label: 'Comptabilité TVA', icon: <Percent className="w-4 h-4" /> },
];

const AdvancedSettings = () => {
  const [activeTab, setActiveTab] = useState<TabId>('stock');
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [isAutoSave] = useState(true);
  const [stockRemovalReasons, setStockRemovalReasons] = useState([]);
  const [saveStatus, setSaveStatus] = useState<any>("none");
  const [initialized, setInitialized] = useState(false);
  const [isParent, setIsParent] = useState(true);
  const [firstUpdate, setFirstUpdate] = useState(false);
  const [autoCreateSleepingCustomerTasks, setAutoCreateSleepingCustomerTasks] = useState(true);
  const [sleepingCustomerThresholdDays, setSleepingCustomerThresholdDays] = useState(30);

  const sameIds = (a?: string[], b?: string[]) =>
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((x) => b.includes(x));

 // Get my info -> setStockRemovalReasons
  const initUser = async () => {
    try {
      const res = await getMyInfo();
      if (res?.data) {
        const fromDb = Array.isArray(res.data.stockRemovalReasons) ? res.data.stockRemovalReasons : [];
        setStockRemovalReasons(fromDb);
        setSelectedOption(fromDb);
        // User is parent if they don't have an ownerId
        setIsParent(!res.data.ownerId);
        if (typeof res.data.autoCreateSleepingCustomerTasks === 'boolean') {
          setAutoCreateSleepingCustomerTasks(res.data.autoCreateSleepingCustomerTasks);
        }
        if (res.data.sleepingCustomerThresholdDays) {
          setSleepingCustomerThresholdDays(res.data.sleepingCustomerThresholdDays);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setInitialized(true);
    }
  };

useEffect(() => {
  initUser();
}, []);

useEffect(() => {
  if (!initialized || !isAutoSave || !firstUpdate) return;

  setSaveStatus('saving');
  const delayDebounceFn = setTimeout(async () => {
    try {
      const { data, error } = await updateMyInfo({ stockRemovalReasons });
      if (error || !data) {
        setSaveStatus('error');
        return;
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('none'), 1500);
    } catch (err) {
      console.error(err);
      setSaveStatus('error');
    }
  }, 600);
  return () => clearTimeout(delayDebounceFn);
}, [initialized, isAutoSave, stockRemovalReasons]);

 useEffect(() => {
  console.log('Raisons de suppression du stock mises à jour :', stockRemovalReasons);
}, [stockRemovalReasons]);


const handleStockReason = (id: string[]) => {

  if (!isParent) {
    setSaveStatus('error');
    toast.error("Seul le compte principal peut modifier cette configuration.");
    return;
  }

  if (!firstUpdate) setFirstUpdate(true);
  setSelectedOption(id);
  setStockRemovalReasons(id);
};

const handleToggleAutoSleepingTasks = async (checked: boolean) => {
  setAutoCreateSleepingCustomerTasks(checked);
  setSaveStatus('saving');
  const { data, error } = await updateMyInfo({ autoCreateSleepingCustomerTasks: checked });
  if (error || !data) {
    setSaveStatus('error');
    return;
  }
  setSaveStatus('saved');
  setTimeout(() => setSaveStatus('none'), 1500);
};

const handleSleepingThresholdBlur = async () => {
  setSaveStatus('saving');
  const { data, error } = await updateMyInfo({ sleepingCustomerThresholdDays });
  if (error || !data) {
    setSaveStatus('error');
    return;
  }
  setSaveStatus('saved');
  setTimeout(() => setSaveStatus('none'), 1500);
};

  
  const deductionOptions = [
    {
      id: ['recurrent-order', 'command-page-order', 'manual-order'],
      title: 'À la commande',
      description: 'Le stock est défalqué automatiquement lors de la création d\'une commande récurrente, via la page de commande ou manuellement par l\'administrateur.',
      icon: <Calendar className="w-6 h-6" />,
      timing: 'Automatique - Création de commande'
    },
    {
      id: ['prepared-production'],
      title: 'À la préparation d\'une production',
      description: 'Le stock est défalqué lorsque la production est terminée depuis la page de production.',
      icon: <MousePointer className="w-6 h-6" />,
      timing: 'Automatique - Validation d\'une production'
    },
    {
      id: ['prepared-order'],
      title: 'À la préparation d\'une commande',
      description: 'Le stock est défalqué lorsque la commande est marquée comme préparée depuis la page de gestion des commandes.',
      icon: <Package className="w-6 h-6" />,
      timing: 'Automatique - Commande préparée'
    },
    {
      id: ['delivered-order'],
      title: 'À la livraison',
      description: 'Le stock est défalqué seulement quand la livraison est confirmée',
      icon: <Truck className="w-6 h-6" />,
      timing: 'Automatique - Confirmation de livraison'
    }
  ];

  

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex justify-between">
                <SettingsMenu path="/settings/advanced" />
                <SaveStatus status={saveStatus} />
            </div>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <h1 className="text-gray-900 font-semibold text-2xl mb-1">Paramètres avancés</h1>
          <p className="text-gray-600 text-sm mb-6">
            Configuration du stock, tags produits, zones de stockage et comptabilité TVA
          </p>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-toola-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {activeTab === 'stock' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="bg-toola-500 bg-opacity-10 p-3 rounded-lg">
                  <Package className="w-6 h-6 text-toola-500" />
                </div>
                <div>
                  <h2 className="text-gray-900 font-semibold text-lg mb-2">Moment de défalcation du stock</h2>
                  <p className="text-gray-600 text-sm">
                    Choisissez à quel moment le stock doit être automatiquement réduit lors du processus de commande.
                    Cette configuration affectera tous les produits de votre inventaire.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {deductionOptions.map((option) => {
                  const selected = sameIds(selectedOption, option.id);
                  return (
                    <div
                      key={option.id.join("-")}
                      className={`relative border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:border-toola-500 hover:shadow-md ${
                        selected
                          ? 'border-toola-500 bg-toola-500 bg-opacity-5'
                          : 'border-gray-200'
                      }`}
                      onClick={() => handleStockReason(option.id)}
                    >
                      <input
                        type="radio"
                        name="deduction"
                        value={option.id}
                        checked={selected}
                        onChange={() => handleStockReason(option.id)}
                        className="absolute top-3 right-3 w-4 h-4 text-toola-500 focus:ring-toola-500 focus:ring-2"
                      />
                      <div className="flex items-start gap-3 pr-6">
                        <div className={`p-2 rounded-lg shrink-0 ${
                          selected
                            ? 'bg-toola-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {option.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-gray-900 font-semibold text-sm">{option.title}</h3>
                            {selected && <Check className="w-4 h-4 text-toola-500 shrink-0" />}
                          </div>
                          <p className="text-gray-600 text-xs leading-relaxed mb-2">{option.description}</p>
                          <div className="inline-flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded-full">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                            <span className="text-gray-600 text-[11px]">{option.timing}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'crm' && (
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
            <div className="flex items-start gap-4">
              <div className="bg-toola-500 bg-opacity-10 p-3 rounded-lg">
                <BellOff className="w-6 h-6 text-toola-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-gray-900 font-semibold text-lg mb-2">Relance automatique des clients inactifs</h2>
                <p className="text-gray-600 text-sm mb-4">
                  Une tâche de relance est créée chaque jour pour chaque client actif n'ayant passé aucune commande
                  depuis le nombre de jours défini ci-dessous. Désactivez cette option si vous préférez gérer ces
                  relances vous-même.
                </p>
                <label className="inline-flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={autoCreateSleepingCustomerTasks}
                    onChange={(e) => handleToggleAutoSleepingTasks(e.target.checked)}
                    className="w-4 h-4 text-toola-500 focus:ring-toola-500 focus:ring-2 rounded"
                  />
                  <span className="text-sm text-gray-800">Créer automatiquement ces tâches de relance</span>
                </label>
                {autoCreateSleepingCustomerTasks && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Relancer après</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={sleepingCustomerThresholdDays}
                      onChange={(e) => setSleepingCustomerThresholdDays(Number(e.target.value))}
                      onBlur={handleSleepingThresholdBlur}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-toola-500"
                    />
                    <span className="text-sm text-gray-600">jours sans commande</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tags' && <TagList />}
        {activeTab === 'zones' && <ZoneList />}
        {activeTab === 'vat' && <VatAccountingCodeList />}
      </div>
    </div>
  );
};



export default AdvancedSettings;
